import { useEffect, useState } from "react";
import { getOnboardingStatus, getProfileRequired, patchProfileRequired } from "../services/api";
import Card from "../components/Card";
import Button from "../components/Button";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Input } from "../components/Input";
import { loadSession, saveSession } from "../auth";
import RouteDeclarationSection from "../components/RouteDeclarationSection";

export default function Onboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [profileReq, setProfileReq] = useState(null);
  const [form, setForm] = useState({ job_title: "", start_date: "", birth_place: "" });
  const [startDateUnknown, setStartDateUnknown] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Prefer onboarding status cached in session (set by the route guard).
        // Handle both normalized (`{ is_complete, tasks }`) and legacy wrapper
        // shapes (`{ ok: true, onboarding: { ... } }`).
        const sess = loadSession();
        const rawSessOnb = sess?.onboarding;
        const sessOnb = rawSessOnb?.onboarding ?? rawSessOnb; // unwrap if needed
        if (sessOnb) {
          if (!alive) return;
          setData(sessOnb);
          if (sessOnb?.is_complete) {
            navigate("/dashboard", { replace: true });
            return;
          }
          if (alive) setLoading(false);
          return;
        }

        // Fallback: fetch directly if not present in session
        const res = await getOnboardingStatus();
        if (!alive) return;
        setData(res);
        try {
          const s = loadSession() || {};
          s.onboarding = res;
          saveSession(s);
        } catch (_) { /* ignore */ }
        if (res?.is_complete) {
          navigate("/dashboard", { replace: true });
          return;
        }

        // Do not eagerly fetch profile-required here; a data-driven effect
        // will load profile-required only when `complete_profile` is the
        // currently active blocking task. This avoids rendering multiple
        // onboarding forms at the same time.
      } catch (e) {
        if (!alive) return;
        setError(e?.message || t("onboarding.error", "A apărut o eroare."));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // If onboarding status becomes complete after a refetch, redirect to dashboard
    if (data?.is_complete) {
      navigate("/dashboard", { replace: true });
    }
  }, [data, navigate]);
  

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setServerError(null);
    setFormErrors({});

    const next = {
      job_title: String(form.job_title || "").trim(),
      birth_place: String(form.birth_place || "").trim() || undefined,
    };
    const sd = String(form.start_date || "").trim();
    if (startDateUnknown) {
      // Explicitly send null when the user indicates they don't know the date
      next.start_date = null;
    } else if (sd) {
      next.start_date = sd; // omit when empty
    }

    const errs = {};
    if (!next.job_title) errs.job_title = t("onboarding.job_title_required", "Funcția este obligatorie.");
    if (Object.keys(errs).length) {
      setFormErrors(errs);
      return;
    }

    setSaving(true);
    try {
      await patchProfileRequired(next);
      // refetch onboarding status
      const refreshed = await getOnboardingStatus();
      setData(refreshed);
      try {
        const s = loadSession() || {};
        s.onboarding = refreshed;
        saveSession(s);
      } catch (_) { /* ignore */ }
      if (refreshed?.is_complete) {
        navigate("/dashboard", { replace: true });
        return;
      }
      // Do not eagerly re-fetch profile-required here; the active task
      // selection effect will load or clear it based on refreshed status.
    } catch (e) {
      setServerError(e?.message || t("onboarding.save_failed", "Salvarea a eșuat"));
    } finally {
      setSaving(false);
    }
  }

  // renderStatus not used — keep helper inline if needed later

  const tasks = Array.isArray(data?.tasks) ? [...data.tasks] : [];
  tasks.sort((a, b) => {
    // blocking tasks first
    const blocksDiff = Number(b.blocks_access) - Number(a.blocks_access);
    if (blocksDiff !== 0) return blocksDiff;
    // pending first
    if (a.status === "pending" && b.status !== "pending") return -1;
    if (b.status === "pending" && a.status !== "pending") return 1;
    // fallback to sort_order then title
    const so = (a.sort_order || 0) - (b.sort_order || 0);
    if (so !== 0) return so;
    return (a.title || "").localeCompare(b.title || "");
  });

  // Select the current active blocking task (only blocking & incomplete tasks are candidates).
  const nonRenderableStatuses = new Set(["completed", "cancelled", "canceled"]);
  const isIncompleteStatus = (s) => !nonRenderableStatuses.has(String(s || "").toLowerCase());

  const blockingIncomplete = tasks.filter((t) => t.blocks_access && isIncompleteStatus(t.status));
  // Stable ordering: prefer numeric sort_order when present, otherwise preserve server order
  const withIndex = blockingIncomplete.map((t, i) => ({ t, i }));
  withIndex.sort((a, b) => {
    const aHas = typeof a.t.sort_order === "number";
    const bHas = typeof b.t.sort_order === "number";
    if (aHas && bHas) return (a.t.sort_order || 0) - (b.t.sort_order || 0);
    if (aHas && !bHas) return -1;
    if (!aHas && bHas) return 1;
    return a.i - b.i;
  });
  const activeTask = withIndex.length ? withIndex[0].t : null;

  // When the active task changes, load profile-required only if the
  // active task is `complete_profile`. Clear profileReq when not active.
  useEffect(() => {
    let alive = true;
    (async () => {
      if (!activeTask) {
        if (alive) setProfileReq(null);
        return;
      }
      if (activeTask.code !== "complete_profile") {
        if (alive) setProfileReq(null);
        return;
      }
      // If we already have profileReq loaded for this active task, skip.
      if (profileReq) return;
      try {
        const pr = await getProfileRequired();
        if (!alive) return;
        setProfileReq(pr);
        setForm({ job_title: pr.fields?.job_title ?? "", start_date: pr.fields?.start_date ?? "", birth_place: pr.fields?.birth_place ?? "" });
        // Reset the frontend-only unknown checkbox when loading server value
        setStartDateUnknown(false);
      } catch (e) {
        if (!alive) return;
        setServerError(e?.message || t("onboarding.error", "A apărut o eroare."));
      }
    })();
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTask?.code]);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-slate-900 mb-2">{t("onboarding.title", "Bun venit în ZeroRiscuri")}</h1>
      <p className="text-slate-500 mb-6">{t("onboarding.subtitle", "Pentru a continua, trebuie să finalizezi pașii obligatorii de configurare a contului.")}</p>

      <Card>
        {loading ? (
          <div className="text-sm text-slate-500">{t("onboarding.loading", "Se încarcă…")}</div>
        ) : error ? (
          <div className="text-sm text-red-600">{error}</div>
        ) : (
          <div>
            {activeTask && activeTask.code === "complete_profile" && profileReq && (
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-slate-900">{t("onboarding.form_title", "Completează datele personale")}</h2>
                <p className="text-sm text-slate-500 mb-4">{t("onboarding.form_help", "Aceste informații sunt necesare pentru configurarea contului și pentru documentele generate în platformă.")}</p>

                <form onSubmit={handleSubmit} className="grid sm:grid-cols-2 gap-4">
                  <div className="sm:col-span-2">
                    <label className="block text-xs text-slate-500 font-medium mb-1">
                      {t("onboarding.job_title_label", "Funcția (conform cod COR)")}
                      <span className="text-red-600 ml-1">*</span>
                    </label>
                    <Input aria-required="true" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} />
                    {formErrors.job_title && <div className="text-sm text-red-600 mt-1">{formErrors.job_title}</div>}
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs text-slate-500 font-medium mb-1">
                      {t("onboarding.birth_place_label", "Locul nașterii")}
                    </label>
                    <Input value={form.birth_place} onChange={(e) => setForm({ ...form, birth_place: e.target.value })} />
                  </div>

                  <div className="sm:col-span-2">
                    <label className="block text-xs text-slate-500 font-medium mb-1">
                      {t("onboarding.start_date_label", "Data angajării (prima zi de activitate)")}
                    </label>
                    <Input type="date" disabled={startDateUnknown} aria-disabled={startDateUnknown} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
                    {formErrors.start_date && <div className="text-sm text-red-600 mt-1">{formErrors.start_date}</div>}
                    <div className="mt-2">
                      <label className="inline-flex items-center text-sm text-slate-700">
                        <input type="checkbox" className="h-4 w-4 rounded border-slate-200" checked={startDateUnknown} onChange={(e) => { const checked = !!e.target.checked; setStartDateUnknown(checked); if (checked) setForm({ ...form, start_date: "" }); }} />
                        <span className="ml-2">{t("onboarding.start_date_unknown", "Nu sunt sigur de data angajării")}</span>
                      </label>
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <Button type="submit" disabled={saving}>{saving ? t("onboarding.saving", "Se salvează...") : t("onboarding.save_continue", "Salvează și continuă")}</Button>
                  </div>
                </form>

                {serverError && <div className="text-sm text-red-600 mt-3">{serverError}</div>}
              </div>
            )}

            {/* Route declaration section: show for complete_route_declaration or sign_route_declaration tasks */}
            {activeTask && (
              activeTask.code === "complete_route_declaration" ||
              activeTask.task_type === "route_declaration" ||
              activeTask.code === "sign_route_declaration" ||
              activeTask.task_type === "sign_route_declaration"
            ) && (
              <div className="mb-6">
                <RouteDeclarationSection onRefetch={setData} />
              </div>
            )}

            {/*tasks.length ? (
              <ul className="divide-y divide-slate-100">
                {tasks.map((task) => (
                  <li key={task.id} className="py-4 flex items-center gap-4">
                    <div className="flex-1">
                      <div className="font-semibold text-slate-900">{task.title}</div>
                      <div className="text-sm text-slate-500">{task.description}</div>
                      <div className="text-xs text-slate-400 mt-1">{renderStatus(task.status)} {task.blocks_access ? (<span className="ml-2 text-xs font-medium text-red-600">{t("onboarding.blocks_access", "Blochează accesul")}</span>) : null}</div>
                    </div>
                    <div className="flex-shrink-0">
                      {task.code === "complete_profile" ? (
                        <Link to="/profile">
                          <Button>{t("onboarding.complete_profile_action", "Completează datele")}</Button>
                        </Link>
                      ) : (
                        <Button variant="ghost" disabled>
                          {t("onboarding.complete_profile_placeholder", "Formularul de completare a datelor va fi disponibil în pasul următor.")}
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-sm text-slate-500 py-4 text-center">{t("onboarding.none", "Nu sunt pași de onboarding.")}</div>
            )*/}
          </div>
        )}
      </Card>
    </div>
  );
}
